import * as React from "react";
import IShareDetails from "./IShareDetails";
import ToolTip from "@appComponents/ToolTip/ToolTip";
import { noLeadingZeros } from "@market/features/helpers/functions";
import { numberWithDelimiter } from "@features/utils/functions";
import CurrencyElement from "@appComponents/CurrencyElement/CurrencyElement";
import Big from "big.js";
import QuestionMark from "@appComponents/QuestionMark/QuestionMark";
import { MAIN_CURRENCY } from "@bundles/consts";

import * as style from "./ShareDetailsStyle.module.scss";
import SmallPriceChart from "@appComponents/SmallPriceChart/SmallPriceChart";

const ShareDetails: React.FunctionComponent<IShareDetails> = (props) => {
  const lastSharePrice = props.lastSharePrice;

  const estimatedCurrentAsset = () => {
    const { orderShares } = props;

    if (lastSharePrice && orderShares) {
      return (
        <CurrencyElement
          value={new Big(lastSharePrice).times(props.assetsShares.total)}
          currency={MAIN_CURRENCY}
        />
      );
    }

    return "-";
  };

  const nominalAssetValue = () => {
    const { detailedCompanyAsset, orderShares } = props;
    const price = detailedCompanyAsset.initial_share_price;

    return orderShares && price ? (
      <CurrencyElement
        value={new Big(price).times(props.assetsShares.total)}
        currency={MAIN_CURRENCY}
      />
    ) : (
      "-"
    );
  };

  const getPriceFloor = () => {
    const priceFloor = props.detailedCompanyAsset.price_floor;

    return priceFloor ? (
      <CurrencyElement value={priceFloor} currency={MAIN_CURRENCY} />
    ) : (
      "-"
    );
  };

  return (
    <div className={style.assetDetails}>
      <div className={style.assetDetailsGroup}>
        <dl>
          <dt>
            Tradable <br /> SPV Shares
            <ToolTip
              className={style.secondaryMarketTooltip}
              text={`Total amount of shares: ${props.assetsShares.total}${" "}
                (includes shares with an open sell order and/or locked-in shares).`}
            >
              <QuestionMark isWhite={true} marginLeft={5} />
            </ToolTip>
          </dt>
          <dd>
            {numberWithDelimiter(noLeadingZeros(props.assetsShares.sellable))}
          </dd>
        </dl>
        <dl>
          <dt>
            Nominal value <br /> of your holding
            <ToolTip
              className={style.secondaryMarketTooltip}
              text={`Value of the holding at the last known finance round.
              This may be higher than your purchase price if the entity has raised
              further finance after you have invested.`}
            >
              <QuestionMark isWhite={true} marginLeft={5} />
            </ToolTip>
          </dt>
          <dd>{nominalAssetValue()}</dd>
        </dl>
        <dl>
          <dt>
            Estimated current <br /> value of your holding
            <ToolTip
              className={style.secondaryMarketTooltip}
              text={`Current estimate based on most recent valuations, if available. This is advisory only and does
                  not guarantee trading performance on the secondary market, which may be
                  higher or lower than this figure.`}
            >
              <QuestionMark isWhite={true} marginLeft={5} />
            </ToolTip>
          </dt>
          {<dd>{estimatedCurrentAsset()}</dd>}
        </dl>
        <dl className={style.separatorWrapper}>
          <div className={style.separator} />
        </dl>
        <dl>
          <dt>
            Price
            <br />
            Floor
            <ToolTip
              className={style.secondaryMarketTooltip}
              text={"Price from the last known valuation event."}
            >
              <QuestionMark isWhite={true} marginLeft={5} />
            </ToolTip>
          </dt>
          <dd>{getPriceFloor()}</dd>
        </dl>
        {!!props.detailedCompanyAsset.price_cap_enabled && (
          <dl>
            <dt>
              Price
              <br />
              Cap
            </dt>
            <dd>
              <CurrencyElement
                value={props.detailedCompanyAsset.price_cap ?? 0}
                currency={MAIN_CURRENCY}
              />
            </dd>
          </dl>
        )}
        <dl>
          <dt>
            Initial <br /> share price
          </dt>
          <dd>
            {props.detailedCompanyAsset.initial_share_price ? (
              <CurrencyElement
                value={props.detailedCompanyAsset.initial_share_price}
                currency={MAIN_CURRENCY}
              />
            ) : (
              "-"
            )}
          </dd>
        </dl>
        <dl>
          <dt>
            Last <br /> trade price
          </dt>
          {
            <dd>
              {props.last_trade_price ? (
                <CurrencyElement
                  value={props.last_trade_price}
                  currency={MAIN_CURRENCY}
                />
              ) : (
                "-"
              )}
            </dd>
          }
        </dl>
        <dl className={style.chartHolder}>
          <dt>
            Current market <br />
            performance:
          </dt>
          <dd onClick={props.scrollToTrades}>
            <SmallPriceChart
              assetId={props.detailedCompanyAsset.id}
              tradesList={props.tradesList}
            />
          </dd>
        </dl>
      </div>
    </div>
  );
};

export default ShareDetails;
